/**
 * UserSettingsStorage.tsx — Sección "Almacenamiento y respaldo" de Ajustes.
 *
 * QUÉ HACE:
 *  - Muestra cuánto espacio local queda, con desglose por módulo (Caja,
 *    Inventario, Auditoría, Personal, …) y nivel de riesgo.
 *  - Permite descargar el respaldo cifrado del negocio (ZIP, reutilizando
 *    `businessBackup.service`), liberar espacio sin borrar datos
 *    (`compactLocalStorage`) y restablecer la app de fábrica
 *    (`factoryResetLocalData`) como último recurso.
 *
 * QUÉ INVOCA: getStorageUsageReport, getOriginStorageEstimate, getMirrorSizeBytes,
 *             createEncryptedBusinessBackup, downloadReportExport,
 *             compactLocalStorage, factoryResetLocalData.
 * QUÉ NO DEBE HACER:
 *  - No tocar localStorage directo (todo pasa por lib/persistence).
 *  - No borrar datos sin confirmación explícita del usuario.
 */
import { useEffect, useMemo, useState } from "react";
import toast from "react-hot-toast";
import { Database, HardDrive, RefreshCw, Trash2 } from "lucide-react";
import { Button } from "@/components/ui";
import { ConfirmationModal } from "@/components/common/ConfirmationModal";
import { FloatingLabelInput } from "@/components/ui/FloatingLabelInput";
import {
  formatBytes,
  getMirrorSizeBytes,
  getOriginStorageEstimate,
  getStorageUsageReport,
  type OriginStorageEstimate,
  type StorageModuleUsage,
  type StorageUsageLevel,
} from "@/lib/persistence/storageUsage";
import { createEncryptedBusinessBackup } from "@/features/reporting/services/businessBackup.service";
import { downloadReportExport } from "@/features/reporting/services/reportExport.service";
import {
  compactLocalStorage,
  factoryResetLocalData,
} from "@/lib/persistence/factoryReset";

const LEVEL_LABELS: Record<StorageUsageLevel, string> = {
  ok: "Bien",
  warning: "Aviso",
  alert: "Alerta",
  critical: "Crítico",
};

const LEVEL_CLASSES: Record<StorageUsageLevel, string> = {
  ok: "bg-emerald-500",
  warning: "bg-amber-500",
  alert: "bg-orange-500",
  critical: "bg-red-600",
};

const LEVEL_TEXT: Record<StorageUsageLevel, string> = {
  ok: "text-emerald-700 dark:text-emerald-300",
  warning: "text-amber-700 dark:text-amber-300",
  alert: "text-orange-700 dark:text-orange-300",
  critical: "text-red-700 dark:text-red-300",
};

export function UserSettingsStorage() {
  const [report, setReport] = useState(() => getStorageUsageReport());
  const [mirrorBytes, setMirrorBytes] = useState(0);
  const [origin, setOrigin] = useState<OriginStorageEstimate | null>(null);
  const [backupSecret, setBackupSecret] = useState("");
  const [backupLoading, setBackupLoading] = useState(false);
  const [compactLoading, setCompactLoading] = useState(false);
  const [resetOpen, setResetOpen] = useState(false);
  const [resetLoading, setResetLoading] = useState(false);

  useEffect(() => {
    void getMirrorSizeBytes().then(setMirrorBytes);
    void getOriginStorageEstimate().then(setOrigin);
  }, []);

  const refresh = () => setReport(getStorageUsageReport());

  const { totalBytes, capBytes, percentUsed, level, modules, keyCount } =
    report;

  const remaining = Math.max(0, capBytes - totalBytes);

  const sortedModules = useMemo<StorageModuleUsage[]>(
    () =>
      [...modules].sort((a, b) => b.bytes - a.bytes).filter((m) => m.bytes > 0),
    [modules],
  );

  const handleBackup = async () => {
    const secret = backupSecret.trim();
    if (!secret) {
      toast.error(
        "Ingresa el NIT o cédula del negocio para cifrar el respaldo.",
      );
      return;
    }
    setBackupLoading(true);
    try {
      const backup = await createEncryptedBusinessBackup(secret);
      const completed = await downloadReportExport(backup.file);
      if (completed) {
        toast.success(
          `Respaldo descargado: ${backup.datasetCount} conjuntos y ${backup.recordCount} registros.`,
          { duration: 6000 },
        );
      } else {
        toast.error("No se pudo entregar el respaldo en este dispositivo.");
      }
      setBackupSecret("");
    } catch (err) {
      toast.error(
        err instanceof Error ? err.message : "No se pudo generar el respaldo.",
      );
    } finally {
      setBackupLoading(false);
    }
  };

  const handleCompact = () => {
    setCompactLoading(true);
    try {
      const freed = compactLocalStorage();
      refresh();
      if (freed > 0) {
        toast.success(`Espacio liberado: ${formatBytes(freed)}`, {
          duration: 6000,
        });
      } else {
        toast("No hay espacio adicional que liberar automáticamente.");
      }
    } catch (err) {
      toast.error("No se pudo liberar espacio.");
    } finally {
      setCompactLoading(false);
    }
  };

  const handleReset = async () => {
    setResetLoading(true);
    try {
      await factoryResetLocalData();
      toast.success("Datos restablecidos. La app se recargará…", {
        duration: 3000,
      });
      window.setTimeout(() => window.location.reload(), 1500);
    } catch (err) {
      toast.error("No se pudo restablecer los datos.");
      setResetLoading(false);
    }
  };

  const maxModuleBytes = Math.max(
    1,
    ...sortedModules.map((m) => m.bytes),
    remaining,
  );

  return (
    <div className="px-3 py-3 space-y-2.5">
      <p className="px-1 pb-0.5 text-[11px] text-gray-500 dark:text-slate-400 leading-relaxed">
        Todo vive en este dispositivo. El espacio no es infinito: aquí ves
        cuánto queda, qué lo ocupa y cómo protegerlo o liberarlo.
      </p>

      {/* Uso total */}
      <div className="p-3 rounded-xl bg-gray-50 dark:bg-slate-700/50 border border-gray-100 dark:border-slate-600">
        <div className="flex items-center justify-between">
          <span className="text-xs font-bold text-gray-800 dark:text-slate-200">
            Espacio local usado
          </span>
          <span
            className={`text-[10px] font-bold px-1.5 py-0.5 rounded-full ${LEVEL_TEXT[level]} bg-black/5 dark:bg-black/20`}
          >
            {LEVEL_LABELS[level]} · {percentUsed}%
          </span>
        </div>
        <div className="mt-2 h-2 rounded-full bg-gray-200 dark:bg-slate-600 overflow-hidden">
          <div
            className={`h-full rounded-full transition-all ${LEVEL_CLASSES[level]}`}
            style={{ width: `${Math.min(100, percentUsed)}%` }}
          />
        </div>
        <p className="mt-1.5 text-[10px] text-gray-500 dark:text-slate-400">
          {formatBytes(totalBytes)} de ~{formatBytes(capBytes)} · quedan{" "}
          {formatBytes(remaining)} · {keyCount} claves
        </p>
      </div>

      {/* Desglose por módulo */}
      <div className="p-3 rounded-xl bg-white dark:bg-slate-800 border border-gray-100 dark:border-slate-600 space-y-1.5">
        <p className="text-[10px] font-bold uppercase tracking-widest text-gray-400 dark:text-slate-500 flex items-center gap-1">
          <HardDrive size={10} /> Qué ocupa el espacio
        </p>
        {sortedModules.length === 0 ? (
          <p className="text-[11px] text-gray-400 dark:text-slate-500">
            Aún no hay datos guardados.
          </p>
        ) : (
          sortedModules.map((m) => (
            <div key={m.module} className="space-y-0.5">
              <div className="flex items-center justify-between text-[11px]">
                <span className="text-gray-700 dark:text-slate-300 font-medium">
                  {m.module}
                </span>
                <span className="text-gray-400 dark:text-slate-500 tabular-nums">
                  {formatBytes(m.bytes)}
                </span>
              </div>
              <div className="h-1 rounded-full bg-gray-100 dark:bg-slate-600/60 overflow-hidden">
                <div
                  className="h-full rounded-full bg-primary-500/70"
                  style={{
                    width: `${Math.max(2, (m.bytes / maxModuleBytes) * 100)}%`,
                  }}
                />
              </div>
            </div>
          ))
        )}
      </div>

      {/* Información técnica */}
      {(mirrorBytes > 0 || origin) && (
        <p className="px-1 text-[10px] text-gray-400 dark:text-slate-500">
          {mirrorBytes > 0
            ? `Respaldo en IndexedDB: ${formatBytes(mirrorBytes)}. `
            : ""}
          {origin
            ? `Cuota total del dispositivo: ${formatBytes(
                origin.usageBytes,
              )} de ${formatBytes(origin.quotaBytes)}.`
            : ""}
        </p>
      )}

      {/* Respaldo cifrado */}
      <div className="p-3 rounded-xl bg-blue-50 dark:bg-blue-900/20 border border-blue-100 dark:border-blue-800/40 space-y-2">
        <p className="text-xs font-bold text-blue-800 dark:text-blue-200 flex items-center gap-1">
          <Database size={13} /> Descargar respaldo cifrado
        </p>
        <p className="text-[10px] text-blue-600/90 dark:text-blue-300/80 leading-snug">
          Crea un ZIP cifrado con TODO el negocio (restaurable). Úsalo antes de
          liberar espacio o restablecer.
        </p>
        <div className="flex gap-1.5">
          <FloatingLabelInput
            label="NIT o cédula"
            value={backupSecret}
            onChange={(e) => setBackupSecret(e.target.value)}
            type="password"
            className="flex-1"
          />
          <Button
            variant="primary"
            size="sm"
            onClick={() => void handleBackup()}
            disabled={backupLoading || !backupSecret.trim()}
            className="self-end flex-shrink-0"
          >
            {backupLoading ? "Generando…" : "Descargar"}
          </Button>
        </div>
      </div>

      {/* Liberar espacio */}
      <div className="p-3 rounded-xl bg-emerald-50 dark:bg-emerald-900/20 border border-emerald-100 dark:border-emerald-800/40 space-y-2">
        <p className="text-xs font-bold text-emerald-800 dark:text-emerald-200 flex items-center gap-1">
          <RefreshCw size={13} /> Liberar espacio (sin borrar datos)
        </p>
        <p className="text-[10px] text-emerald-700/90 dark:text-emerald-300/80 leading-snug">
          Recorta historiales internos (auditoría, métricas) que crecen solos.
          No borra ventas, cierres, créditos ni inventario.
        </p>
        <Button
          variant="secondary"
          size="sm"
          onClick={handleCompact}
          disabled={compactLoading}
          className="w-full"
        >
          {compactLoading ? "Liberando…" : "Liberar espacio"}
        </Button>
      </div>

      {/* Restablecer de fábrica */}
      <div className="p-3 rounded-xl bg-red-50 dark:bg-red-950/40 border border-red-100 dark:border-red-900/50 space-y-2">
        <p className="text-xs font-bold text-red-800 dark:text-red-200 flex items-center gap-1">
          <Trash2 size={13} /> Restablecer datos de fábrica
        </p>
        <p className="text-[10px] text-red-700/90 dark:text-red-300/80 leading-snug">
          Último recurso de emergencia: borra TODO de este dispositivo y la app
          vuelve a su estado inicial. Descarga tu respaldo primero.
        </p>
        <Button
          variant="danger"
          size="sm"
          onClick={() => setResetOpen(true)}
          disabled={resetLoading}
          className="w-full"
        >
          {resetLoading ? "Restableciendo…" : "Restablecer todo"}
        </Button>
      </div>

      <ConfirmationModal
        isOpen={resetOpen}
        onClose={() => setResetOpen(false)}
        onConfirm={() => void handleReset()}
        variant="danger"
        title="¿Restablecer datos de fábrica?"
        confirmText="Sí, borrar todo"
        cancelText="Cancelar"
        message={
          <>
            Se borrarán{" "}
            <strong>
              todas las ventas, cierres, créditos, inventario, empleados y
              configuraciones
            </strong>{" "}
            de este dispositivo, y la app volverá a su estado inicial de demo.
            <br />
            <br />
            Esta acción no se puede deshacer.{" "}
            <strong>Descarga un respaldo cifrado antes</strong> (botón de
            arriba) si quieres conservar la información.
          </>
        }
      />
    </div>
  );
}

export default UserSettingsStorage;
